import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { OauthRoomsConfig } from ".";
import { useStartRoomOAuth } from "./useStartRoomOAuth";
import { openExternalUrl } from "../../tools/open-external-url";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";

/**
 * The partner membership of an OAuth room (SFCTCV, docs/partner-oauth-rooms.md):
 * revalidated on every entry into the room, which also rolls the background
 * token renewal forward. `none` for a room with no partner sign-in; otherwise
 * the refusal to show (`null` while the membership holds) and the reader's two
 * ways out of it.
 */
export function useRoomOAuthMembership(roomId: string):
  | { kind: "none" }
  | {
      kind: "oauth";
      partnerName: string;
      errorCode: string | null;
      connect: () => Promise<void>;
      later: () => void;
    } {
  const history = useHistory();
  const { currentUser } = useCurrentUser();
  const [oauthErrorCode, setOauthErrorCode] = useState<string | null>(null);
  const startRoomOAuth = useStartRoomOAuth();
  // Active visibilitychange listener installed by the Renew flow, kept on a
  // ref so the unmount effect can detach it if the user navigates away while
  // the SFCTCV renewal tab is still open.
  const oauthRenewVisibilityHandlerRef = useRef<(() => void) | null>(null);

  const oauthRoom = OauthRoomsConfig.find((room) => room.id === roomId);

  async function checkOauthAccess() {
    if (currentUser === null) return;
    if (oauthRoom === undefined) return;

    // Hit the OAuth server for fresh membership state. `revalidateUserInfo`
    // handles transparent token refresh + transient-error fallback, so by the
    // time it resolves we either have an actionable error code or a clean
    // pass.
    const result = await oauthRoom.manager.revalidateUserInfo();
    if (result.success) {
      setOauthErrorCode(null);
    } else {
      setOauthErrorCode(
        result.errorCode === undefined ? "NOT_AUTHENTICATED" : result.errorCode
      );
    }
  }

  async function handleOAuthConnect() {
    if (oauthRoom === undefined) return;

    if (oauthErrorCode === "MEMBERSHIP_INACTIVE") {
      // Keep the modal visible while the user heads to SFCTCV to fix their
      // subscription; the visibilitychange listener below revalidates when
      // they return and closes the modal on success.
      // `window.open` opens nothing inside a native WebView. The shells route
      // an external link to the system browser through this helper, which is
      // also what makes the visibilitychange below fire on the way back.
      openExternalUrl(oauthRoom.manager.renewUrl);
      // Replace any prior listener (e.g. user clicked Renew twice) so we
      // never leak two handlers.
      const prev = oauthRenewVisibilityHandlerRef.current;
      if (prev !== null) document.removeEventListener("visibilitychange", prev);
      function handleVisibilityChange() {
        if (document.visibilityState !== "visible") return;
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        oauthRenewVisibilityHandlerRef.current = null;
        void checkOauthAccess();
      }
      oauthRenewVisibilityHandlerRef.current = handleVisibilityChange;
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return;
    }

    setOauthErrorCode(null);
    oauthRoom.manager.logout();
    const outcome = await startRoomOAuth(oauthRoom, roomId);
    // `oauthErrorCode` is the only thing gating this page. The reader closed
    // the sheet without signing in, and the logout above means they now hold
    // no credential at all, so the gate has to come back up: otherwise
    // cancelling dismisses it and leaves the partner room on screen until the
    // page remounts.
    if (outcome.kind === "abandoned") setOauthErrorCode("NOT_AUTHENTICATED");
  }

  function handleOAuthLater() {
    setOauthErrorCode(null);
    history.push("/discovery");
  }

  // Check oauth access
  useEffect(() => {
    if (currentUser === null) return;
    void checkOauthAccess();
  }, [currentUser, roomId]);

  // Detach a pending Renew-flow visibility listener if we unmount before the
  // user returns to the app.
  useEffect(() => {
    return () => {
      const handler = oauthRenewVisibilityHandlerRef.current;
      if (handler !== null) {
        document.removeEventListener("visibilitychange", handler);
        oauthRenewVisibilityHandlerRef.current = null;
      }
    };
  }, []);

  if (oauthRoom === undefined) return { kind: "none" };
  return {
    kind: "oauth",
    partnerName: oauthRoom.name,
    errorCode: oauthErrorCode,
    connect: handleOAuthConnect,
    later: handleOAuthLater,
  };
}
